import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import './BgPattern.css';
import { composeCellProps, cursorProximityEffect, scrollCellEffect, verticalSizeGradientEffect } from './gridEffects';
import {
    drawSparkle,
    HOME_BG_PATTERN_FADE_ANGLE,
    HOME_BG_PATTERN_FADE_END,
    HOME_BG_PATTERN_VIGNETTE_INNER,
    HOME_BG_PATTERN_VIGNETTE_OUTER,
    HOME_BG_PATTERN_VIGNETTE_X,
    HOME_BG_PATTERN_VIGNETTE_Y,
    HOME_CROSS_PATTERN,
    HOME_GRID_EFFECTS_CONFIG,
    crossHalfReach,
    fitGridX,
    tilePx,
} from './patterns';

const findScrollRoot = (el) => {
    let node = el?.parentElement;
    while (node) {
        const { overflowY } = getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
    }
    return null;
};

const buildEffects = (effectsConfig) => {
    const list = [];
    const gradient = effectsConfig.verticalGradient;
    if (gradient?.enabled !== false && gradient) list.push(verticalSizeGradientEffect(gradient));
    const scroll = effectsConfig.scroll;
    if (scroll?.enabled !== false) list.push(scrollCellEffect(scroll));
    list.push(cursorProximityEffect(effectsConfig.cursor));
    return list;
};

const BgPattern = ({
    config = HOME_CROSS_PATTERN,
    effectsConfig = HOME_GRID_EFFECTS_CONFIG,
    className = '',
}) => {
    const canvasRef = useRef(null);
    const cursorRef = useRef(null);
    const scrollRef = useRef(0);
    const rafRef = useRef(0);
    const effects = useMemo(() => buildEffects(effectsConfig), [effectsConfig]);

    const syncCanvasLayout = useCallback((canvas, contentW, contentH, pad) => {
        canvas.style.left = `${-pad}px`;
        canvas.style.top = `${-pad}px`;
        canvas.style.width = `${contentW + pad * 2}px`;
        canvas.style.height = `${contentH + pad * 2}px`;
    }, []);

    const paint = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const host = canvas.parentElement;
        const contentW = host?.offsetWidth ?? 0;
        const contentH = host?.offsetHeight ?? 0;
        if (!contentW || !contentH) return;

        // Skip repaints while the host is fully outside the viewport (e.g. scrolled
        // past the hero). Scroll/mousemove still fire globally, but there's nothing to draw.
        const hostRect = host.getBoundingClientRect();
        if (hostRect.bottom <= 0 || hostRect.top >= window.innerHeight) return;

        const stepY = tilePx(config);
        const { cols, stepX } = fitGridX(contentW, config);
        const rows = Math.ceil(contentH / stepY) + 1;
        const cellSize = Math.min(stepX, stepY);
        const cellScale = cellSize / config.tileSize;
        const pad = crossHalfReach(config, cellScale);
        syncCanvasLayout(canvas, contentW, contentH, pad);

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const canvasW = contentW + pad * 2;
        const canvasH = contentH + pad * 2;

        const dpr = window.devicePixelRatio || 1;
        const bufW = Math.round(canvasW * dpr);
        const bufH = Math.round(canvasH * dpr);
        if (canvas.width !== bufW || canvas.height !== bufH) {
            canvas.width = bufW;
            canvas.height = bufH;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        const base = {
            stroke: config.stroke,
            strokeWidth: config.strokeWidth,
            strokeLinecap: config.strokeLinecap,
            bgFill: config.bgFill,
            opacity: config.opacity,
        };

        const rect = canvas.getBoundingClientRect();
        let cursor = null;
        if (cursorRef.current) {
            cursor = {
                x: cursorRef.current.x - rect.left - pad,
                y: cursorRef.current.y - rect.top - pad,
            };
        }

        const effectCtx = {
            cursor,
            scrollY: scrollRef.current,
            baseCrossInset: config.crossInset,
            contentH,
        };

        ctx.clearRect(0, 0, canvasW, canvasH);
        ctx.save();
        ctx.translate(pad, pad);

        for (let row = 0; row < rows; row += 1) {
            for (let col = 0; col < cols; col += 1) {
                const centerX = col * stepX;
                const centerY = row * stepY;
                const cell = {
                    col,
                    row,
                    x: centerX,
                    y: centerY,
                    centerX,
                    centerY,
                };
                const props = composeCellProps(base, cell, effects, effectCtx);
                const inset = props.crossInset ?? config.crossInset;
                const drawScale = props.drawScale ?? 1;
                const rotation = props.rotation ?? 0;
                ctx.save();
                ctx.translate(centerX, centerY);
                ctx.rotate(rotation);
                const drawCellScale = cellScale * drawScale;
                ctx.scale(drawCellScale, drawCellScale);
                ctx.translate(-config.tileSize / 2, -config.tileSize / 2);
                drawSparkle(ctx, config.tileSize, inset, props);
                ctx.restore();
            }
        }

        ctx.restore();
    }, [config, effects, syncCanvasLayout]);

    const schedulePaint = useCallback(() => {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(paint);
    }, [paint]);

    useEffect(() => {
        schedulePaint();
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ro = new ResizeObserver(schedulePaint);
        const host = canvas.parentElement;
        if (host) ro.observe(host);
        else ro.observe(canvas);

        const scrollRoot = findScrollRoot(canvas) || window;
        const readScroll = () => {
            scrollRef.current = scrollRoot === window
                ? window.scrollY
                : scrollRoot.scrollTop;
        };
        readScroll();

        const onScroll = () => {
            readScroll();
            schedulePaint();
        };
        const onMove = (e) => {
            cursorRef.current = { x: e.clientX, y: e.clientY };
            schedulePaint();
        };
        const onLeave = () => {
            cursorRef.current = null;
            schedulePaint();
        };

        scrollRoot.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('mousemove', onMove, { passive: true });
        document.documentElement.addEventListener('mouseleave', onLeave);

        return () => {
            ro.disconnect();
            scrollRoot.removeEventListener('scroll', onScroll);
            window.removeEventListener('mousemove', onMove);
            document.documentElement.removeEventListener('mouseleave', onLeave);
            cancelAnimationFrame(rafRef.current);
        };
    }, [schedulePaint]);

    return (
        <canvas
            ref={canvasRef}
            className={`bg-pattern ${className}`.trim()}
            style={{
                '--bg-pattern-fade-end': HOME_BG_PATTERN_FADE_END,
                '--bg-pattern-fade-angle': HOME_BG_PATTERN_FADE_ANGLE,
                '--bg-pattern-vignette-x': HOME_BG_PATTERN_VIGNETTE_X,
                '--bg-pattern-vignette-y': HOME_BG_PATTERN_VIGNETTE_Y,
                '--bg-pattern-vignette-inner': HOME_BG_PATTERN_VIGNETTE_INNER,
                '--bg-pattern-vignette-outer': HOME_BG_PATTERN_VIGNETTE_OUTER,
            }}
            aria-hidden="true"
        />
    );
};

export default BgPattern;
